// =============================================================================
// RemittanceDetailPage — detail + acknowledge action (finance)
// =============================================================================

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiGet, apiPost, getApiError } from '../../../lib/api';
import { Card } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Input } from '../../../components/Input';
import { Modal } from '../../../components/Modal';
import { EmailDocButton } from '../../../components/EmailDocButton';
import { Money } from '../../../components/Money';
import { StatusPill } from '../../../components/StatusPill';
import { PageScaffold } from '../../../components/PageScaffold';
import { Can } from '../../../lib/permissions';

interface JournalEntryDetail {
  number: string;
  date: string;
  lines: Array<{
    id: string;
    debit: string;
    credit: string;
    account: { code: string; name: string };
  }>;
}

interface RemDetail {
  id: string;
  number: string;
  status: 'PENDING' | 'ACKNOWLEDGED' | 'DISPUTED';
  source: 'DELIVERY_COD' | 'SHOP_DRAWER';
  claimedAmount: string;
  actualAmount: string | null;
  submittedAt: string;
  acknowledgedAt: string | null;
  disputedReason: string | null;
  notes: string | null;
  driver: { name: string };
  acknowledgedBy: { name: string } | null;
  shop: { id: string; code: string; name: string } | null;
  lines: Array<{
    id: string;
    amount: string;
    payment: {
      id: string;
      method: string;
      reference: string | null;
      order: {
        id: string;
        number: string;
        customer: { name: string; companyName: string | null; type: 'individual' | 'company' };
      };
    } | null;
    sourceAccount: { id: string; name: string; account: { code: string; name: string } } | null;
  }>;
  journalEntry: JournalEntryDetail | null;
  submissionJournal: JournalEntryDetail | null;
}

export function RemittanceDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const [showAck, setShowAck] = useState(false);

  const detail = useQuery({
    queryKey: ['driver-remittance', id],
    queryFn: () => apiGet<RemDetail>(`/driver-remittances/${id}`),
    enabled: !!id,
  });

  const downloadPdf = (): void => {
    if (!detail.data) return;
    const r = detail.data;
    void (async () => {
      try {
        const res = await api.get(`/driver-remittances/${r.id}/pdf`, { responseType: 'blob' });
        const blob = new Blob([res.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${r.number}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 30_000);
      } catch (e) {
        console.error(e);
      }
    })();
  };

  if (detail.isLoading) {
    return (
      <PageScaffold title="Remittance" back={{ to: '/remittances', label: 'Remittances' }}>
        <div className="text-ink-3">Loading…</div>
      </PageScaffold>
    );
  }
  if (!detail.data) {
    return (
      <PageScaffold title="Remittance" back={{ to: '/remittances', label: 'Remittances' }}>
        <div className="text-ink-3">Not found.</div>
      </PageScaffold>
    );
  }
  const r = detail.data;
  const tone: 'warning' | 'positive' | 'negative' =
    r.status === 'ACKNOWLEDGED' ? 'positive' : r.status === 'DISPUTED' ? 'negative' : 'warning';

  const HEADER_ACTIONS = (
    <>
      <Button variant="secondary" onClick={downloadPdf}>
        Download PDF
      </Button>
      <EmailDocButton
        endpoint={`/driver-remittances/${r.id}/email`}
        selfDefault
        title="Email remittance"
        subtitle="Send the remittance PDF by email."
      />
      {r.status === 'PENDING' && (
        <Can permission="remittances.acknowledge">
          <Button onClick={() => setShowAck(true)}>Count + acknowledge</Button>
        </Can>
      )}
    </>
  );

  return (
    <PageScaffold
      title={r.number}
      back={{ to: '/remittances', label: 'Remittances' }}
      actions={HEADER_ACTIONS}
    >
      <div className="flex items-center gap-2 flex-wrap">
        <StatusPill tone={tone}>{r.status.toLowerCase()}</StatusPill>
        <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded bg-paper-2 border border-rule text-ink-2">
          {r.source === 'SHOP_DRAWER' ? `Shop · ${r.shop?.code ?? '—'}` : 'Delivery COD'}
        </span>
        <span className="text-xs text-ink-3">
          Driver: <span className="text-ink-2">{r.driver.name}</span>
          {' · '}submitted {new Date(r.submittedAt).toLocaleString('en-MU')}
        </span>
      </div>

      {r.status === 'DISPUTED' && r.disputedReason && (
        <Card bodyClassName="px-6 py-4 bg-negative-soft border-negative/30">
          <div className="text-sm text-negative font-medium">Dispute</div>
          <div className="text-xs text-ink-2 mt-1">{r.disputedReason}</div>
          <div className="text-xs text-ink-3 mt-2">
            Resolve by issuing a Journal Voucher to settle the gap.
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card
            title={r.source === 'SHOP_DRAWER' ? 'Cash handed over' : 'Payments in this bundle'}
            bodyClassName="p-0"
          >
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
                  <th className="px-6 py-2 font-medium">
                    {r.source === 'SHOP_DRAWER' ? 'Source' : 'Order'}
                  </th>
                  <th className="px-6 py-2 font-medium">
                    {r.source === 'SHOP_DRAWER' ? 'GL account' : 'Customer'}
                  </th>
                  <th className="px-6 py-2 font-medium">
                    {r.source === 'SHOP_DRAWER' ? 'Type' : 'Method'}
                  </th>
                  <th className="px-6 py-2 font-medium text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {r.lines.map((l) => {
                  if (l.payment) {
                    const c = l.payment.order.customer;
                    const display = c.type === 'company' ? (c.companyName ?? c.name) : c.name;
                    return (
                      <tr key={l.id} className="border-b border-rule-2">
                        <td className="px-6 py-3">
                          <Link
                            to={`/orders/${l.payment.order.id}`}
                            className="kc-mono text-xs text-ink hover:text-accent"
                          >
                            {l.payment.order.number}
                          </Link>
                        </td>
                        <td className="px-6 py-3 text-ink">{display}</td>
                        <td className="px-6 py-3 text-xs text-ink-3 uppercase">
                          {l.payment.method}
                        </td>
                        <td className="px-6 py-3 text-right">
                          <Money value={l.amount} />
                        </td>
                      </tr>
                    );
                  }
                  const acc = l.sourceAccount?.account;
                  return (
                    <tr key={l.id} className="border-b border-rule-2">
                      <td className="px-6 py-3 text-ink">
                        {r.shop?.name ?? l.sourceAccount?.name ?? 'Drawer'}
                      </td>
                      <td className="px-6 py-3">
                        {acc ? (
                          <>
                            <span className="kc-mono text-xs text-ink-3">{acc.code}</span>
                            <span className="ml-2 text-ink-2">{acc.name}</span>
                          </>
                        ) : (
                          <span className="text-ink-3">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-xs text-ink-3 uppercase">Drawer sweep</td>
                      <td className="px-6 py-3 text-right">
                        <Money value={l.amount} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>

          {r.submissionJournal && (
            <JournalCard
              title="Submission entry · drawer → in transit"
              entry={r.submissionJournal}
            />
          )}
          {r.journalEntry && (
            <JournalCard title="Acknowledge entry · in transit → HQ cash" entry={r.journalEntry} />
          )}
        </div>

        <div className="space-y-4">
          <Card title="Totals">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-3">Driver claimed</span>
                <Money value={r.claimedAmount} />
              </div>
              {r.actualAmount && (
                <div className="flex justify-between">
                  <span className="text-ink-3">Finance counted</span>
                  <Money value={r.actualAmount} />
                </div>
              )}
              {r.actualAmount && Number(r.actualAmount) !== Number(r.claimedAmount) && (
                <div className="flex justify-between text-negative font-medium pt-1 border-t border-rule">
                  <span>Discrepancy</span>
                  <Money value={Number(r.actualAmount) - Number(r.claimedAmount)} />
                </div>
              )}
            </div>
          </Card>
          {r.acknowledgedBy && (
            <Card title="Acknowledged by">
              <div className="text-ink">{r.acknowledgedBy.name}</div>
              <div className="text-xs text-ink-3 mt-1">
                {r.acknowledgedAt && new Date(r.acknowledgedAt).toLocaleString('en-MU')}
              </div>
            </Card>
          )}
          {r.notes && (
            <Card title="Notes">
              <p className="text-sm text-ink-2 whitespace-pre-wrap">{r.notes}</p>
            </Card>
          )}
        </div>
      </div>

      {showAck && (
        <AckModal
          id={r.id}
          claimed={Number(r.claimedAmount)}
          onClose={() => setShowAck(false)}
          onDone={() => {
            setShowAck(false);
            qc.invalidateQueries({ queryKey: ['driver-remittance', id] });
          }}
        />
      )}
    </PageScaffold>
  );
}

function JournalCard({ title, entry }: { title: string; entry: JournalEntryDetail }): JSX.Element {
  return (
    <Card title={title} bodyClassName="p-0">
      <div className="px-6 py-3 border-b border-rule text-xs text-ink-3 flex items-center gap-4">
        <span className="kc-mono text-ink">{entry.number}</span>
        <span>{new Date(entry.date).toLocaleDateString('en-MU')}</span>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-ink-3 bg-paper-2 border-y border-rule">
            <th className="px-6 py-2 font-medium">Account</th>
            <th className="px-3 py-2 font-medium text-right">Debit</th>
            <th className="px-6 py-2 font-medium text-right">Credit</th>
          </tr>
        </thead>
        <tbody>
          {entry.lines.map((l) => (
            <tr key={l.id} className="border-b border-rule-2">
              <td className="px-6 py-2">
                <span className="kc-mono text-xs text-ink-3">{l.account.code}</span>
                <span className="ml-2 text-ink-2">{l.account.name}</span>
              </td>
              <td className="px-3 py-2 text-right kc-mono">
                {l.debit !== '0' ? (
                  <Money value={l.debit} />
                ) : (
                  <span className="text-ink-3">—</span>
                )}
              </td>
              <td className="px-6 py-2 text-right kc-mono">
                {l.credit !== '0' ? (
                  <Money value={l.credit} />
                ) : (
                  <span className="text-ink-3">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function AckModal({
  id,
  claimed,
  onClose,
  onDone,
}: {
  id: string;
  claimed: number;
  onClose: () => void;
  onDone: () => void;
}): JSX.Element {
  const [actual, setActual] = useState(String(claimed));
  const [notes, setNotes] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const ack = useMutation({
    mutationFn: () =>
      apiPost(`/driver-remittances/${id}/acknowledge`, {
        actualAmount: Number(actual),
        notes: notes || undefined,
      }),
    onSuccess: onDone,
    onError: (e) => setErr(getApiError(e).message),
  });
  const diff = Number(actual) - claimed;
  return (
    <Modal
      title="Count the cash"
      subtitle="Same amount → posts to GL. Different → marks DISPUTED for manual JV settlement."
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              ack.mutate();
            }}
            disabled={ack.isPending}
          >
            {ack.isPending ? 'Posting…' : diff === 0 ? 'Acknowledge + post' : 'Mark disputed'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="text-sm">
          Driver claimed:{' '}
          <strong className="text-ink">
            <Money value={claimed} />
          </strong>
        </div>
        <Input
          label="Actual amount counted"
          type="number"
          step="0.01"
          min="0"
          value={actual}
          onChange={(e) => setActual(e.target.value)}
        />
        {diff !== 0 && (
          <div className="text-xs text-warning">
            Discrepancy:{' '}
            <strong>
              <Money value={diff} className="text-warning" />
            </strong>{' '}
            — will be marked DISPUTED.
          </div>
        )}
        <Input label="Notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        {err && <div className="text-xs text-negative">{err}</div>}
      </div>
    </Modal>
  );
}
